import { useCallback, useState } from 'react';

/**
 * Checkbox selection state for Accounts list views. Selection persists across
 * pages/filters (ids accumulate) until cleared or a bulk action succeeds.
 */
export function useRowSelection() {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** Select or deselect every id on the current page. */
  const setPage = useCallback((ids: string[], checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  return {
    selected,
    ids: Array.from(selected),
    count: selected.size,
    has: (id: string) => selected.has(id),
    allSelected: (ids: string[]) => ids.length > 0 && ids.every((id) => selected.has(id)),
    toggle,
    setPage,
    clear,
  };
}

export type RowSelection = ReturnType<typeof useRowSelection>;
